import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useDebounce from '../hooks/useDebounce';
import SearchList from './SearchList';
import Loader from './Loader';
import SuggestedList from './SuggestedList';

const SearchForm = ({ lookupUrl }) => {
    // Semi-unique ID for this component, used for accessiblity IDs and labels
    const SEARCH_INPUT_ID = `search_query_${Math.ceil(Math.random() * 10000)}`;
    // state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemQuery, setItemQuery] = useState('');
    const [items, setItems] = useState([]);

    const debouncedSearch = useDebounce(searchTerm, 500);

    const handleChange = (e) => {
        setSearchTerm(e.target.value);
        if (e.target.value === '' && e.target.value.trim().length <= 2) {
            setItems([]);
        }
    };

    const clearValue = () => {
        setSearchTerm('');
        setItems([]);
        setIsLoading(true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const populateSuggestion = (buttonText) => {
        const clickedBtn = buttonText.trim();
        if (buttonText) {
            setSearchTerm(clickedBtn);
        } else {
            setSearchTerm('');
        }
    };

    /**
     * Respond to attempts to submit the search form.
     *
     * @param {Submit Event} evt Event that triggered the submission
     *
     * @returns void
     */
    const onFormSubmit = (evt) => {
        evt.preventDefault();
        /**
         * Just prevent the form submission.
         * The results update automatically as a search term
         * is entered.
         */
    };

    useEffect(() => {
        const getData = async () => {
            setError(null);

            try {
                if (
                    debouncedSearch !== null &&
                    debouncedSearch.trim().length > 2 &&
                    debouncedSearch !== ''
                ) {
                    setIsLoading(true);

                    const response = await fetch(
                        `${lookupUrl}&query=${debouncedSearch}&num_ranks=999`,
                        {
                            method: 'GET',
                            headers: {
                                Accept: 'application/json',
                            },
                        }
                    );

                    if (!response.ok) {
                        console.log('something went wrong');
                    }

                    const data = await response.json();

                    if (debouncedSearch === '') {
                        setItems([]);
                    }

                    const { results } = data.response.resultPacket;
                    const dataQuery = data.question.query;

                    setItemQuery(dataQuery);
                    setItems(results);
                    setIsLoading(false);
                }
            } catch (err) {
                setError(error.message || 'Something went wrong!');
                // setIsLoading(true);
            }
        };

        if (debouncedSearch) {
            getData();
        }
    }, [debouncedSearch, itemQuery]);

    return (
        <>
            <div className="search-form__wrapper">
                <div className="search-form__container">
                    <div className="search-form__row">
                        <form
                            className="search-form__form"
                            onSubmit={onFormSubmit}
                        >
                            <div className="search-form__input-block">
                                <svg className="svg-icon svg-icon--search">
                                    <use href="#rse-search-page-glass" />
                                </svg>
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                <label
                                    htmlFor={SEARCH_INPUT_ID}
                                    className="sr-only"
                                >
                                    Enter a search query
                                </label>
                                <input
                                    onKeyDown={handleKeyDown}
                                    type="text"
                                    name="search"
                                    id={SEARCH_INPUT_ID}
                                    placeholder="Search"
                                    onChange={handleChange}
                                    value={searchTerm}
                                />
                                <button
                                    type="button"
                                    className="desktop-search-cancel"
                                >
                                    <span className="sr-only">Clear search query</span>
                                    <svg
                                        onClick={clearValue}
                                        className="svg-icon svg-icon--close"
                                    >
                                        <use href="#rse-search-close" />
                                    </svg>
                                </button>
                                <button className="sr-only" type="submit">
                                    Search results will update automatically as you search
                                </button>
                            </div>
                        </form>

                        {items.length === 0 &&
                        !isLoading &&
                        debouncedSearch.trim().length > 1 ? (
                            <div className="search-form__row search-form__row--noResult">
                                <p
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: `Unfortunately there were no results for <span class="result">"${debouncedSearch}"</span>`,
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>

                    {items.length > 0 && error === null ? (
                        <SearchList
                            data={items}
                            itemQuery={itemQuery}
                            onGetSuggestion={populateSuggestion}
                        />
                    ) : (
                        isLoading &&
                        debouncedSearch !== '' &&
                        debouncedSearch.trim().length > 2 && <Loader />
                    )}

                    {items.length === 0 && error === null && (
                        <SuggestedList onGetSuggestion={populateSuggestion} />
                    )}
                </div>
            </div>
        </>
    );
};

export default SearchForm;

SearchForm.propTypes = {
    lookupUrl: PropTypes.string,
};

SearchForm.defaultProps = {
    lookupUrl:
        'https://www-tac-stage.squiz.cloud/rse2022/_design/integration-points/funnelback/search?collection=tac~sp-road-safety-education',
};
